import { useEffect, useMemo, useRef, useState } from "react";
import { BreakEvenChart } from "./charts/BreakEvenChart";
import { LeakChart } from "./charts/LeakChart";
import { RateStrip } from "./charts/RateStrip";
import { AddressField } from "./components/AddressField";
import { MatchHud } from "./components/MatchHud";
import { PrintProvenance } from "./components/PrintProvenance";
import { RateDuel } from "./components/RateDuel";
import { SeoGuide } from "./components/SeoGuide";
import { campaignFromPage, gapBand, lenderCategory, track } from "./lib/analytics";
import { LENDER_SOURCES, type YbrCategory } from "./data/lender-sources";
import { LENDERS } from "./data/market";
import { useMarket } from "./hooks/useMarket";
import {
  bandCopy,
  bandFromPercent,
  effectiveBalance,
  lvrLabel,
  lvrPercent,
  lvrUsed,
  money,
  moneyExact,
  monthlyRepayment,
  noPrintReason,
  pct,
  rateHint,
  runCheck,
} from "./lib/calc";
import { clearSavedDeal, loadDeal, saveDeal } from "./lib/deal";
import { duelRates, nudgeRate } from "./lib/duel";
import { compliance, licenceLine, phoneHref, whatsappHref } from "./lib/compliance";
import { loadPrefs, savePrefs, type DisplayPrefs } from "./lib/prefs";
import { stayScript, stayScriptBeats } from "./lib/script";
import { shouldBlockLeadSubmit, submitLead } from "./lib/leadClient";
import { packFailedMessage, packPartialMessage, PACK_ON_ITS_WAY } from "./lib/leadCopy";
import { leadError } from "./lib/validate";
import type { LeadKind, LeadPayload, LoanInput, LvrBand, MarketProduct } from "./types";
import { PAGE_H1, PAGE_H1_EM } from "./content";
import { APP_VERSION } from "./version";

const start: LoanInput = {
  lender: "CBA",
  rate: 6.5,
  balance: 650000,
  years: 25,
  occupancy: "owner",
  repayment: "pi",
  lvr: "80",
  offset: false,
};

function readPack(): boolean {
  try {
    return sessionStorage.getItem("azure-pack") === "1";
  } catch {
    return false;
  }
}

function writePack() {
  try {
    sessionStorage.setItem("azure-pack", "1");
  } catch {
    /* private mode */
  }
}

async function copyText(value: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(value);
    return true;
  } catch {
    try {
      const node = document.createElement("textarea");
      node.value = value;
      node.setAttribute("readonly", "");
      node.style.position = "fixed";
      node.style.left = "-9999px";
      document.body.appendChild(node);
      node.select();
      const ok = document.execCommand("copy");
      node.remove();
      return ok;
    } catch {
      return false;
    }
  }
}

async function sendLead(payload: LeadPayload) {
  return submitLead(payload);
}

const remembered = loadDeal();

export default function App() {
  const { market, loading } = useMarket();
  const [loan, setLoan] = useState<LoanInput>(remembered?.loan ?? start);
  const [tab, setTab] = useState<"stay" | "switch">("stay");
  const [copied, setCopied] = useState(false);
  const [named, setNamed] = useState<MarketProduct | null>(null);
  const [lead, setLead] = useState({ name: "", email: "", mobile: "" });
  const [privacy, setPrivacy] = useState(false);
  const [creditGuide, setCreditGuide] = useState(false);
  const [sent, setSent] = useState<LeadKind | null>(null);
  const [wide, setWide] = useState(() => window.matchMedia("(min-width: 980px)").matches);
  const [loanOpen, setLoanOpen] = useState(true);
  const [prefs, setPrefs] = useState<DisplayPrefs>(loadPrefs);
  const [packUnlocked, setPackUnlocked] = useState(readPack);
  const [rateText, setRateText] = useState(remembered?.rateText ?? String(start.rate));
  const [yearsText, setYearsText] = useState(remembered?.yearsText ?? String(start.years));
  const [valueText, setValueText] = useState(remembered?.valueText ?? "");
  const [lvrMode, setLvrMode] = useState<"know" | "calc">("know");
  const [sending, setSending] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [hit, setHit] = useState(0);
  const [moved, setMoved] = useState(false);
  const [rivalMode, setRivalMode] = useState<"print" | "offer">("print");
  const [offerText, setOfferText] = useState("");
  const [offsetText, setOffsetText] = useState(remembered?.offsetText ?? "");
  const [fromSave] = useState(() => Boolean(remembered));
  const [keyboardOpen, setKeyboardOpen] = useState(false);
  const started = useRef(false);
  const gapSeen = useRef(false);
  const packStarted = useRef(false);
  const dealtOnce = useRef(false);
  const computedLvr = lvrPercent(loan.balance, loan.propertyValue ?? 0);
  const quotedLvr = lvrUsed(loan);
  const statementHint = rateHint(loan.rate);
  const emptyReason = noPrintReason(loan, market.products);

  const result = useMemo(() => runCheck(loan, market.products), [loan, market.products]);
  const pick =
    named && result?.eligible.some((p) => p.lender === named.lender && p.product === named.product)
      ? named
      : result?.best;
  const pickMonthly =
    result && pick
      ? monthlyRepayment(loan.balance, pick.rate, loan.years, loan.repayment)
      : 0;
  const offerRate = Number(offerText);
  const themRate =
    rivalMode === "offer" && offerRate > 0 ? offerRate : pick?.rate ?? 0;
  const duel = useMemo(
    () =>
      pick
        ? duelRates({
            youRate: loan.rate,
            themRate,
            youBalance: effectiveBalance(loan),
            themBalance: loan.balance,
            years: loan.years,
            repayment: loan.repayment,
            switchingCost: result?.switchingCost,
          })
        : null,
    [loan, pick, themRate, result?.switchingCost],
  );
  const yearlyVsPick = duel?.yearlyGap ?? (result && pick ? (result.currentMonthly - pickMonthly) * 12 : 0);
  const trio = result && pick ? result.comparables : [];
  const isSample =
    !fromSave &&
    loan.rate === start.rate &&
    loan.balance === start.balance &&
    loan.years === start.years;
  const beats = result && pick ? stayScriptBeats(loan, result, pick) : [];
  const script = result && pick ? stayScript(loan, result, pick) : "";
  const live = market.source !== "snapshot";
  const call = phoneHref();
  const whatsapp = whatsappHref();

  useEffect(() => {
    const mq = window.matchMedia("(min-width: 980px)");
    const sync = () => setWide(mq.matches);
    sync();
    mq.addEventListener("change", sync);
    return () => mq.removeEventListener("change", sync);
  }, []);

  useEffect(() => {
    setNamed(null);
    setCopied(false);
    setSent(null);
    setMoved(false);
  }, [loan.lender, loan.rate, loan.balance, loan.years, loan.occupancy, loan.repayment, loan.lvr]);

  useEffect(() => {
    setCopied(false);
  }, [script]);

  useEffect(() => {
    savePrefs(prefs);
  }, [prefs]);

  useEffect(() => {
    if (isSample || !loan.rate || !loan.balance || !loan.years) return;
    saveDeal({ loan, rateText, yearsText, valueText, offsetText });
  }, [loan, rateText, yearsText, valueText, offsetText, isSample]);

  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      const node = event.target;
      if (node instanceof HTMLElement) {
        const tag = node.tagName;
        if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT" || node.isContentEditable) {
          return;
        }
      }
      if (event.key === "1") {
        event.preventDefault();
        goDeal();
      }
      if (event.key === "2") {
        event.preventDefault();
        goGap();
      }
      if (event.key === "3") {
        event.preventDefault();
        goMove();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  });

  useEffect(() => {
    if (started.current) return;
    started.current = true;
    track("ratecheck_started");
  }, []);

  useEffect(() => {
    const view = window.visualViewport;
    if (!view) return;
    const sync = () => setKeyboardOpen(view.height < window.innerHeight * 0.72);
    sync();
    view.addEventListener("resize", sync);
    return () => view.removeEventListener("resize", sync);
  }, []);

  useEffect(() => {
    if (!result) return;
    setHit((n) => n + 1);
  }, [yearlyVsPick, result]);

  useEffect(() => {
    if (!result || !pick || gapSeen.current) return;
    gapSeen.current = true;
    track("gap_calculated", {
      occupancy: loan.occupancy,
      lvrBand: loan.lvr,
      gapBand: gapBand(result.gap),
      lenderCategory: lenderCategory(loan.lender),
    });
    track("cited_print_viewed", {
      occupancy: loan.occupancy,
      lvrBand: loan.lvr,
      gapBand: gapBand(result.gap),
    });
  }, [result, pick, loan.occupancy, loan.lvr, loan.lender]);

  useEffect(() => {
    if (dealtOnce.current) return;
    if (!(loan.rate && loan.balance && loan.years) || isSample) return;
    dealtOnce.current = true;
    track("deal_completed", {
      occupancy: loan.occupancy,
      lvrBand: loan.lvr,
      lenderCategory: lenderCategory(loan.lender),
    });
  }, [loan.rate, loan.balance, loan.years, loan.occupancy, loan.lvr, loan.lender, isSample]);

  useEffect(() => {
    if (packStarted.current || packUnlocked || tab !== "stay" || !moved) return;
    packStarted.current = true;
    track("pack_started", { occupancy: loan.occupancy, lvrBand: loan.lvr });
  }, [packUnlocked, tab, moved, loan.occupancy, loan.lvr]);

  function clearDeal() {
    track("sample_cleared");
    clearSavedDeal();
    setLoan({
      ...start,
      rate: 0,
      balance: 0,
      years: 0,
      offset: false,
      offsetBalance: undefined,
    });
    setRateText("");
    setYearsText("");
    setValueText("");
    setOffsetText("");
    setOfferText("");
    setRivalMode("print");
    setLoanOpen(true);
  }

  function goDeal() {
    setLoanOpen(true);
    document.getElementById("loan")?.scrollIntoView({ behavior: "smooth", block: "start" });
    window.setTimeout(() => document.getElementById("rate-input")?.focus(), 240);
  }

  function goGap() {
    document.getElementById("result-heading")?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function goMove() {
    if (!(result && pick && duel)) return;
    openTab("stay");
  }

  function nudgeYou(delta: number) {
    const next = nudgeRate(loan.rate, delta);
    setLoan({ ...loan, rate: next });
    setRateText(next.toFixed(2));
  }

  function nudgeThem(delta: number) {
    const next = nudgeRate(themRate || pick?.rate || 5.5, delta);
    setRivalMode("offer");
    setOfferText(next.toFixed(2));
  }

  function openTab(next: "stay" | "switch") {
    setMoved(true);
    setTab(next);
    track(next === "stay" ? "stay_selected" : "switch_selected", {
      occupancy: loan.occupancy,
      lvrBand: loan.lvr,
      gapBand: gapBand(result?.gap),
    });
    if (next === "stay") track("bank_script_viewed", { occupancy: loan.occupancy, lvrBand: loan.lvr });
    requestAnimationFrame(() => {
      document.getElementById("next")?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }

  async function onLead(kind: LeadKind) {
    if (shouldBlockLeadSubmit(sending, sent, kind)) return;
    const problem = leadError({ ...lead, privacy });
    if (problem) {
      setFormError(problem);
      return;
    }
    setSending(true);
    setFormError(null);
    const props = {
      occupancy: loan.occupancy,
      lvrBand: loan.lvr,
      gapBand: gapBand(result?.gap),
    };
    track("pack_submit_attempt", props);
    track("pack_submitted", props);
    const campaign = campaignFromPage();
    const outcome = await sendLead({
      kind,
      name: lead.name.trim(),
      email: lead.email.trim(),
      mobile: lead.mobile.trim(),
      privacy: true,
      creditGuide,
      consentedAt: new Date().toISOString(),
      loan,
      pick: pick ? { lender: pick.lender, product: pick.product, rate: pick.rate } : null,
      yearlySave: result?.yearlySave ?? null,
      script: kind === "script" ? script : undefined,
      address: loan.address?.trim() || undefined,
      placeId: loan.placeId || undefined,
      propertyValue: loan.propertyValue || undefined,
      ...campaign,
    });
    setSending(false);
    if (outcome.status === "success") {
      track("pack_submit_success", props);
      track("lead_success", props);
      setSent(kind);
      if (kind === "script") {
        writePack();
        setPackUnlocked(true);
      }
      return;
    }
    track("pack_submit_failure", props);
    track("lead_failure", props);
    if (outcome.status === "partial") {
      setFormError(packPartialMessage(compliance.phone));
      return;
    }
    setFormError(packFailedMessage(compliance.phone));
  }

  const showDock = Boolean(result && pick && duel && !wide && !keyboardOpen);

  return (
    <div className={`${prefs.density === "compact" ? "app is-compact" : "app"}${showDock ? " has-dock" : ""}`}>
      <a className="skip" href="#check">
        Skip to rate check
      </a>

      <header className="top">
        <div className="top-inner">
          <a className="brand" href={compliance.parent}>
            Azure Home Loans
            <small>Rate check</small>
          </a>
          <span className="top-links">
            {call ? <a href={call}>0400 77 77 55</a> : null}
            <a href={compliance.parent}>Main site</a>
          </span>
        </div>
      </header>

      <main className="shell">
        <section id="check" className="mast" aria-labelledby="page-title">
          <div className="mast-copy">
            <details className="intel">
              <summary>
                <span className={live ? "live" : "live is-snap"}>
                  {live ? "Verified · live" : "Verified"}
                </span>
                <time dateTime={market.asAtIso}>{market.asAt}</time>
                {loading ? <span>Updating…</span> : null}
              </summary>
              <p>
                General information only. Not a credit check and not credit assistance. Cited
                prints are advertised new-customer variables we have checked on the lender page.
                Credit is subject to lender approval and a responsible-lending assessment.
              </p>
            </details>
            <h1 id="page-title">
              <span className="mast-eyebrow">Stay first</span>
              {PAGE_H1}
              <em> {PAGE_H1_EM}</em>
            </h1>
            <p className="mast-lead" data-seo-direct-answer>
              Azure Home Loans compares the rate on your statement with eligible
              new-customer prints and shows a yearly gap. No credit check. Copper is
              you. Harbour is them.
            </p>
          </div>
          <MatchHud
            dealt={Boolean(loan.rate && loan.balance && loan.years)}
            scored={Boolean(result && pick && duel)}
            moved={moved}
            score={duel ? `${money(duel.yearlyGap)} / year` : null}
            onDeal={goDeal}
            onGap={goGap}
            onMove={goMove}
          />
        </section>

        <div className="stage">
          <div className="stage-rail">
          <div className="stage-side">
          {isSample ? (
            <p className="sample">
              Sample numbers are in so the well is not empty. Type yours.
              <button type="button" onClick={clearDeal}>
                Clear
              </button>
            </p>
          ) : null}
          <details
            id="loan"
            className="loan-fold"
            open={wide || loanOpen}
            onToggle={(e) => setLoanOpen(e.currentTarget.open)}
          >
            <summary>
              Your statement · {loan.lender || "lender"}
              {loan.rate ? ` · ${loan.rate.toFixed(2)}%` : ""}
              {loan.balance ? ` · ${loan.balance.toLocaleString("en-AU")}` : " · amount"}
            </summary>
            <h2 className="loan-title">Your statement</h2>
            <form
              className="loan"
              aria-label="Your current home loan"
              onSubmit={(e) => e.preventDefault()}
            >
              <label className="field">
                <span>Lender</span>
                <select
                  value={loan.lender}
                  autoComplete="organization"
                  onChange={(e) => setLoan({ ...loan, lender: e.target.value })}
                >
                  {LENDERS.map((name) => (
                    <option key={name}>{name}</option>
                  ))}
                </select>
              </label>
              <label className="field">
                <span>Rate (%)</span>
                <input
                  id="rate-input"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-describedby="rate-hint"
                  value={rateText}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^\d.]/g, "");
                    setRateText(next);
                    const n = Number(next);
                    setLoan({ ...loan, rate: next && Number.isFinite(n) ? n : 0 });
                  }}
                  onBlur={() => {
                    if (!loan.rate) return;
                    const fixed = loan.rate.toFixed(2);
                    setRateText(fixed);
                    setLoan({ ...loan, rate: Number(fixed) });
                  }}
                />
              </label>
              <label className="field">
                <span>Loan amount</span>
                <input
                  inputMode="numeric"
                  autoComplete="off"
                  value={loan.balance ? loan.balance.toLocaleString("en-AU") : ""}
                  onChange={(e) => {
                    const balance = Number(e.target.value.replace(/[^\d]/g, ""));
                    const value = loan.propertyValue ?? 0;
                    const next =
                      lvrMode === "calc" && value
                        ? lvrPercent(balance, value)
                        : null;
                    setLoan({
                      ...loan,
                      balance,
                      lvrExact: next ?? undefined,
                      lvr: next != null ? bandFromPercent(next) : loan.lvr,
                    });
                  }}
                />
              </label>
              <label className="field">
                <span>Years left</span>
                <input
                  inputMode="numeric"
                  autoComplete="off"
                  value={yearsText}
                  onChange={(e) => {
                    const next = e.target.value.replace(/[^\d]/g, "");
                    setYearsText(next);
                    const n = Number(next);
                    setLoan({
                      ...loan,
                      years: Number.isFinite(n) ? Math.min(40, Math.max(0, n)) : 0,
                    });
                  }}
                />
              </label>
              <label className="field">
                <span>Offset · optional</span>
                <input
                  inputMode="numeric"
                  autoComplete="off"
                  value={offsetText}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/[^\d]/g, "");
                    const offsetBalance = Number(raw);
                    setOffsetText(
                      offsetBalance ? offsetBalance.toLocaleString("en-AU") : raw,
                    );
                    setLoan({
                      ...loan,
                      offset: offsetBalance > 0,
                      offsetBalance: offsetBalance || undefined,
                    });
                  }}
                />
              </label>
              {loan.offsetBalance ? (
                <p className="hint">
                  Illustrative. We treat {money(loan.offsetBalance)} as not attracting interest.
                  Your contractual repayment may stay the same.
                </p>
              ) : null}
              <fieldset className="field wide">
                <legend>Occupancy</legend>
                <div className="seg">
                  <label>
                    <input
                      type="radio"
                      name="occ"
                      checked={loan.occupancy === "owner"}
                      onChange={() => setLoan({ ...loan, occupancy: "owner" })}
                    />
                    Owner occ
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="occ"
                      checked={loan.occupancy === "investment"}
                      onChange={() => setLoan({ ...loan, occupancy: "investment" })}
                    />
                    Investment
                  </label>
                </div>
              </fieldset>
              <fieldset className="field wide">
                <legend>Repayments</legend>
                <div className="seg">
                  <label>
                    <input
                      type="radio"
                      name="rep"
                      checked={loan.repayment === "pi"}
                      onChange={() => setLoan({ ...loan, repayment: "pi" })}
                    />
                    P&amp;I
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="rep"
                      checked={loan.repayment === "io"}
                      onChange={() => setLoan({ ...loan, repayment: "io" })}
                    />
                    Interest only
                  </label>
                </div>
              </fieldset>
              <fieldset className="field wide">
                <legend>Do you know your LVR?</legend>
                <p className="hint">
                  LVR is your loan balance divided by the property’s value. We do not value the
                  property.
                </p>
                <div className="seg">
                  <label>
                    <input
                      type="radio"
                      name="lvr-mode"
                      checked={lvrMode === "know"}
                      onChange={() => {
                        setLvrMode("know");
                        setLoan({ ...loan, lvrExact: undefined });
                      }}
                    />
                    Enter LVR band
                  </label>
                  <label>
                    <input
                      type="radio"
                      name="lvr-mode"
                      checked={lvrMode === "calc"}
                      onChange={() => setLvrMode("calc")}
                    />
                    Calculate from value
                  </label>
                </div>
              </fieldset>
              {lvrMode === "know" ? (
                <label className="field wide">
                  <span>LVR band</span>
                  <select
                    value={loan.lvr}
                    onChange={(e) =>
                      setLoan({
                        ...loan,
                        lvr: e.target.value as LvrBand,
                        lvrExact: undefined,
                      })
                    }
                  >
                    <option value="60">Under 60%</option>
                    <option value="80">60–80%</option>
                    <option value="90">80–90%</option>
                    <option value="95">90%+</option>
                  </select>
                </label>
              ) : (
                <>
                  <label className="field wide">
                    <span>Your valuation</span>
                    <input
                      inputMode="numeric"
                      autoComplete="off"
                      value={valueText}
                      onChange={(e) => {
                        const raw = e.target.value.replace(/[^\d]/g, "");
                        const propertyValue = Number(raw);
                        setValueText(
                          propertyValue ? propertyValue.toLocaleString("en-AU") : raw,
                        );
                        const next = lvrPercent(loan.balance, propertyValue);
                        setLoan({
                          ...loan,
                          propertyValue: propertyValue || undefined,
                          lvrExact: next ?? undefined,
                          lvr: next != null ? bandFromPercent(next) : loan.lvr,
                        });
                      }}
                    />
                  </label>
                  <p className="hint lvr-readout">
                    {computedLvr != null
                      ? `${computedLvr}% LVR · we quote prints that accept ${computedLvr}%`
                      : "Loan amount ÷ your estimate. We do not invent a bank valuation."}
                  </p>
                </>
              )}
              <div className="addr-fold">
                <AddressField
                  value={loan.address ?? ""}
                  onChange={({ address, placeId }) =>
                    setLoan({ ...loan, address, placeId })
                  }
                />
                <p className="hint">
                  Optional — used to identify the property. We don’t estimate its value.
                </p>
              </div>
              {lvrMode === "know" && !loan.lvrExact ? (
                <p className="hint">
                  We quote the top of {bandCopy(loan.lvr)} so we do not show a cheaper
                  band you might not get. Calculate from a valuation for the exact LVR.
                </p>
              ) : null}
              {statementHint ? (
                <p id="rate-hint" className="hint rate-warn" role="status">
                  {statementHint}
                </p>
              ) : (
                <p id="rate-hint" className="hint">
                  Interest rate is what you pay. A comparison rate folds in some fees on a
                  $150,000 / 25-year example — do not type that here.
                </p>
              )}
            </form>
          </details>
          </div>

          <div className="stage-main">
          {result && pick ? (
            <section className={hit ? "well is-hit" : "well"} aria-labelledby="result-heading">
              <h2 id="result-heading">Stay first</h2>
              {duel ? (
                <RateDuel
                  duel={duel}
                  youName={`${loan.lender}${loan.offsetBalance ? " · after offset" : ""}`}
                  themName={rivalMode === "offer" ? "Typed offer" : pick.lender}
                  rivalMode={rivalMode}
                  offerText={offerText}
                  onRivalMode={setRivalMode}
                  onOfferText={setOfferText}
                  onNudgeYou={nudgeYou}
                  onNudgeThem={nudgeThem}
                />
              ) : null}
              <PrintProvenance
                pick={pick}
                asAt={market.asAt}
                quotedLvr={quotedLvr}
                showComparison={prefs.comparison}
              />
              <RateStrip
                youRate={loan.rate}
                youLabel={loan.lender}
                products={trio}
                selected={pick}
                onSelect={setNamed}
              />
              {yearlyVsPick > 50 ? <LeakChart yearlySave={yearlyVsPick} years={loan.years} /> : null}
              <div className="well-actions">
                <button
                  type="button"
                  className="is-stay"
                  aria-pressed={tab === "stay"}
                  onClick={() => openTab("stay")}
                >
                  Stay
                  <small>Call the bank with a script</small>
                </button>
                <button type="button" aria-pressed={tab === "switch"} onClick={() => openTab("switch")}>
                  Switch
                  <small>Only if they will not match</small>
                </button>
              </div>
            </section>
          ) : (
            <section className="well well-empty" aria-labelledby="result-heading">
              <h2 id="result-heading">Stay first</h2>
              <p className="well-empty-copy">
                Put your statement in first. Copper is you. Harbour is the cited prints.
                {emptyReason ? ` ${emptyReason}` : ""}
              </p>
            </section>
          )}

          {result && pick ? (
            <section className="panel products-panel" aria-labelledby="products-heading">
              <h2 id="products-heading">
                {packUnlocked
                  ? "Prints you can read to the bank"
                  : trio.length <= 1
                    ? "One cited print on this file."
                    : `${trio.length} cited prints. Stay with one.`}
              </h2>
              {!packUnlocked && trio.length <= 1 ? (
                <p className="note">
                  IMB is the only panel lender with a locked print at this LVR. The other 44 sit
                  in the panel below. We do not invent their rates.
                </p>
              ) : null}
              {packUnlocked ? (
                <>
                  <p className="note">
                    Tap a row to drop it into the stay script. Only cited prints. Tune the list to how
                    you like to read.
                  </p>
                  <details className="tune">
                    <summary>Tune this list</summary>
                    <div className="tune-grid">
                      <label>
                        <input
                          type="checkbox"
                          checked={prefs.density === "compact"}
                          onChange={(e) =>
                            setPrefs({ ...prefs, density: e.target.checked ? "compact" : "roomy" })
                          }
                        />
                        Compact
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={prefs.finePrint}
                          onChange={(e) => setPrefs({ ...prefs, finePrint: e.target.checked })}
                        />
                        Small print
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={prefs.sources}
                          onChange={(e) => setPrefs({ ...prefs, sources: e.target.checked })}
                        />
                        Source links
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={prefs.comparison}
                          onChange={(e) => setPrefs({ ...prefs, comparison: e.target.checked })}
                        />
                        Comparison rate
                      </label>
                      <label>
                        <input
                          type="checkbox"
                          checked={prefs.cashback}
                          onChange={(e) => setPrefs({ ...prefs, cashback: e.target.checked })}
                        />
                        Cashback
                      </label>
                    </div>
                  </details>
                  <ul className="products">
                    {result.eligible.map((p) => {
                      const on = pick.lender === p.lender && pick.product === p.product;
                      return (
                        <li key={`${p.lender}-${p.product}-${p.maxLvr}`}>
                          <button
                            type="button"
                            className="product"
                            aria-pressed={on}
                            onClick={() => setNamed(p)}
                          >
                            <span className="who">
                              {p.lender}
                              <small>{p.product}</small>
                            </span>
                            <span className="pct">{pct(p.rate)}</span>
                            <span className="meta">
                              {lvrLabel(p.maxLvr)}
                              {p.minLoan ? ` · min $${p.minLoan.toLocaleString("en-AU")}` : ""}
                              {prefs.comparison ? ` · comparison ${pct(p.comparisonRate)}` : ""}
                              {prefs.cashback && p.cashback
                                ? ` · cashback ${moneyExact(p.cashback)}`
                                : ""}
                            </span>
                            {prefs.finePrint && p.finePrint ? (
                              <span className="print">{p.finePrint}</span>
                            ) : null}
                          </button>
                          {prefs.sources && p.ratesUrl ? (
                            <p className="print-note">
                              Source:{" "}
                              <a href={p.ratesUrl} target="_blank" rel="noreferrer">
                                {p.lender} rates page
                              </a>
                            </p>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                </>
              ) : (
                <>
                  <p className="note">
                    These are cited new-customer prints for this file — not a ranking. Stay first:
                    take one number to your bank. The words to say come after a name, email and
                    mobile.
                  </p>
                  <ul className="products is-cited">
                    {trio.map((p) => {
                      const on = pick.lender === p.lender && pick.product === p.product;
                      return (
                        <li key={`${p.lender}-${p.product}-${p.maxLvr}`}>
                          <button
                            type="button"
                            className={on ? "product is-teaser is-on" : "product is-teaser"}
                            aria-pressed={on}
                            onClick={() => setNamed(p)}
                          >
                            <span className="who">
                              {p.lender}
                              <small>{p.product}</small>
                            </span>
                            <span className="pct">{pct(p.rate)}</span>
                            <span className="meta">{lvrLabel(p.maxLvr)}</span>
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                  <button type="button" className="copy" onClick={() => openTab("stay")}>
                    Get the bank-call pack
                  </button>
                </>
              )}
              <p className="print-note">
                Advertised new-customer variable prints, as at {market.asAt}. Confirm on the lender
                page before you call.
                {market.note ? ` ${market.note}` : ""}
              </p>
            </section>
          ) : null}
          <LenderPanel />
          </div>

          <div className="stage-end">
          {result ? (
            <section id="next" className="panel next" aria-labelledby="next-heading">
              <div className="next-head">
                <h2 id="next-heading">Stay or switch</h2>
                <div className="next-tabs" role="tablist" aria-label="Stay or switch">
                  <button
                    type="button"
                    role="tab"
                    id="tab-stay"
                    aria-controls="next"
                    aria-selected={tab === "stay"}
                    onClick={() => setTab("stay")}
                  >
                    Stay
                  </button>
                  <button
                    type="button"
                    role="tab"
                    id="tab-switch"
                    aria-controls="next"
                    aria-selected={tab === "switch"}
                    onClick={() => setTab("switch")}
                  >
                    Switch
                  </button>
                </div>
              </div>
              {tab === "stay" ? (
                packUnlocked ? (
                <>
                  <p className="note">
                    This is the pack: a named, verified print plus the words to say. Copy it, or
                    email it to yourself, then call the bank. No credit check. If they will not
                    match, switch.
                  </p>
                  <ol className="beats">
                    {beats.map((beat, i) => (
                      <li key={beat.id}>
                        <details open={i === 0}>
                          <summary>
                            {i + 1}. {beat.title}
                          </summary>
                          <p className="beat-hint">{beat.hint}</p>
                          <p className="beat-say">{beat.say}</p>
                        </details>
                      </li>
                    ))}
                  </ol>
                  <button
                    type="button"
                    className="copy"
                    onClick={() => {
                      void copyText(script).then((ok) => setCopied(ok));
                    }}
                  >
                    {copied ? "Copied — take this pack to the bank" : "Copy the bank-call pack"}
                  </button>
                  {sent === "script" ? (
                    <p className="note">{PACK_ON_ITS_WAY}</p>
                  ) : null}
                  {result.verdict !== "competitive" ? (
                    <button type="button" className="ghost" onClick={() => openTab("switch")}>
                      If they won’t match, switch with us
                    </button>
                  ) : null}
                </>
                ) : (
                <>
                  <p className="note">
                    Stay first: take a named print to retention. The words to say, and every other
                    cited print, are in the pack.
                  </p>
                  <PackGate
                    lead={lead}
                    setLead={setLead}
                    privacy={privacy}
                    setPrivacy={setPrivacy}
                    creditGuide={creditGuide}
                    setCreditGuide={setCreditGuide}
                    sending={sending}
                    error={formError}
                    onSubmit={() => void onLead("script")}
                  />
                  {result.verdict !== "competitive" ? (
                    <button type="button" className="ghost" onClick={() => openTab("switch")}>
                      If they won’t match, switch with us
                    </button>
                  ) : null}
                </>
                )
              ) : (
                <>
                  <p className="note">
                    Cost to move {moneyExact(result.switchingCost)} after cashback.
                    {result.breakEvenMonths != null && result.monthlySave > 0
                      ? ` Break-even in about ${result.breakEvenMonths} months.`
                      : ""}
                  </p>
                  {result.monthlySave > 0 ? (
                    <BreakEvenChart
                      monthlySave={result.monthlySave}
                      switchingCost={result.switchingCost}
                      breakEvenMonths={result.breakEvenMonths}
                      years={loan.years}
                    />
                  ) : null}
                  {result.verdict === "competitive" ? (
                    <p className="note">No need to switch on this file. Stay, or leave it.</p>
                  ) : sent === "switch" ? (
                    <p className="note">
                      Received. A broker will only act after this request. That is when credit
                      assistance starts.
                    </p>
                  ) : (
                    <>
                      {call ? (
                        <a className="copy" href={call}>
                          Call {compliance.representative}
                        </a>
                      ) : null}
                      <LeadFields
                        lead={lead}
                        setLead={setLead}
                        privacy={privacy}
                        setPrivacy={setPrivacy}
                        creditGuide={creditGuide}
                        setCreditGuide={setCreditGuide}
                        needGuide
                        sending={sending}
                        error={formError}
                        onSubmit={() => void onLead("switch")}
                        submitLabel="Request a switch"
                      />
                    </>
                  )}
                </>
              )}
            </section>
          ) : null}
          </div>
          </div>
        </div>

        <SeoGuide />

        <footer className="legal">
          <p className="ver">Rate check {APP_VERSION}</p>
          <p>{licenceLine()}</p>
          <p>
            Information on this website is general only and does not consider your objectives,
            financial situation, or needs. Credit is subject to lender approval, fees, and
            eligibility. Seek independent advice before acting. Credit assistance starts only if you
            ask a licensed broker to act.
          </p>
          <p>
            Rates shown are advertised new-customer variable prints we have checked against the
            lender’s own page. A live bank feed (Consumer Data Right) may refresh a number only when
            it matches the same product and LVR. Last read {market.asAt}
            {market.brands ? ` · ${market.brands} banks polled` : ""}. Comparison websites are not
            scraped. Comparison rates assume a $150,000 loan over 25 years. WARNING: this comparison
            rate is true only for the examples given and may not include all fees and charges.
            Different terms, fees or other loan amounts might result in a different comparison rate.
          </p>
          <p>
            Complaints: {compliance.email} or {compliance.phone}. If we cannot resolve a credit
            complaint, you may contact AFCA at{" "}
            <a href="https://www.afca.org.au">afca.org.au</a> or 1800 931 678.
          </p>
          <p className="legal-links">
            <a href="/how-to-check-home-loan-rate.html">How to check your rate</a>
            <a href="/llms.txt">llms.txt</a>
            <a href={compliance.privacyUrl}>Privacy policy</a>
            <a href={compliance.parent}>azurehomeloans.com.au</a>
            {call ? <a href={call}>{compliance.phone}</a> : null}
            {whatsapp ? (
              <a href={whatsapp} rel="noreferrer">
                WhatsApp
              </a>
            ) : null}
            <a href={compliance.complaintsUrl}>Contact</a>
          </p>
        </footer>
      </main>

      {showDock ? (
        <nav className="dock" aria-label="Deal, stay or switch">
          <button type="button" className="is-deal" onClick={goDeal}>
            Deal
            <small>Your statement</small>
          </button>
          <button
            type="button"
            className="is-stay"
            aria-pressed={tab === "stay"}
            onClick={() => openTab("stay")}
          >
            Stay
            <small>Bank script</small>
          </button>
          <button type="button" aria-pressed={tab === "switch"} onClick={() => openTab("switch")}>
            Switch
            <small>If they stall</small>
          </button>
        </nav>
      ) : null}
    </div>
  );
}

const PANEL_CATS: { id: "all" | YbrCategory; label: string }[] = [
  { id: "all", label: "All" },
  { id: "bank", label: "Banks" },
  { id: "non_bank", label: "Non-bank" },
  { id: "specialty", label: "Specialty" },
];

function LenderPanel() {
  const [query, setQuery] = useState("");
  const [cat, setCat] = useState<"all" | YbrCategory>("all");
  const q = query.trim().toLowerCase();
  const rows = LENDER_SOURCES.filter((row) => {
    if (cat !== "all" && row.category !== cat) return false;
    if (!q) return true;
    return row.name.toLowerCase().includes(q) || row.aliases.some((a) => a.includes(q));
  });

  return (
    <details className="source-fold">
      <summary>
        <span className="source-kicker">Panel</span>
        <strong>{LENDER_SOURCES.length} lenders we can place</strong>
      </summary>
      <p className="note">
        Each link is that lender’s public rates or home-loan page. A cheap print is only shown
        above when we have a cited number for your LVR.
      </p>
      <label className="field panel-search">
        <span>Find a lender</span>
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoComplete="off"
        />
      </label>
      <div className="panel-cats" role="tablist" aria-label="Lender type">
        {PANEL_CATS.map((item) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={cat === item.id}
            onClick={() => setCat(item.id)}
          >
            {item.label}
          </button>
        ))}
      </div>
      <ul className="source-list">
        {rows.map((row) => (
          <li key={row.id}>
            <span>{row.name}</span>
            <a href={row.ratesUrl} target="_blank" rel="noreferrer noopener">
              Rates
            </a>
          </li>
        ))}
      </ul>
      {rows.length === 0 ? <p className="hint">No lender matches that search.</p> : null}
    </details>
  );
}

function PackGate({
  lead,
  setLead,
  privacy,
  setPrivacy,
  creditGuide,
  setCreditGuide,
  sending,
  error,
  onSubmit,
}: {
  lead: { name: string; email: string; mobile: string };
  setLead: (next: { name: string; email: string; mobile: string }) => void;
  privacy: boolean;
  setPrivacy: (next: boolean) => void;
  creditGuide: boolean;
  setCreditGuide: (next: boolean) => void;
  sending: boolean;
  error: string | null;
  onSubmit: () => void;
}) {
  return (
    <div className="pack-gate">
      <h3>Want the exact bank-call script and cited print emailed to you?</h3>
      <p className="hint">
        Named lender, interest rate, LVR cap, comparison rate and the words to say. We only email
        after you tick privacy. That is not a credit check.
      </p>
      <LeadFields
        lead={lead}
        setLead={setLead}
        privacy={privacy}
        setPrivacy={setPrivacy}
        creditGuide={creditGuide}
        setCreditGuide={setCreditGuide}
        needGuide={false}
        sending={sending}
        error={error}
        onSubmit={onSubmit}
        submitLabel="Send the pack"
      />
    </div>
  );
}

function LeadFields({
  lead,
  setLead,
  privacy,
  setPrivacy,
  creditGuide,
  setCreditGuide,
  needGuide,
  sending,
  error,
  onSubmit,
  submitLabel,
}: {
  lead: { name: string; email: string; mobile: string };
  setLead: (next: { name: string; email: string; mobile: string }) => void;
  privacy: boolean;
  setPrivacy: (next: boolean) => void;
  creditGuide: boolean;
  setCreditGuide: (next: boolean) => void;
  needGuide: boolean;
  sending: boolean;
  error: string | null;
  onSubmit: () => void;
  submitLabel: string;
}) {
  return (
    <form
      className="lead"
      onSubmit={(e) => {
        e.preventDefault();
        if (!sending) onSubmit();
      }}
    >
      <label className="field">
        <span>Name</span>
        <input
          required
          autoComplete="name"
          value={lead.name}
          onChange={(e) => setLead({ ...lead, name: e.target.value })}
        />
      </label>
      <label className="field">
        <span>Email</span>
        <input
          type="email"
          required
          autoComplete="email"
          inputMode="email"
          value={lead.email}
          onChange={(e) => setLead({ ...lead, email: e.target.value })}
        />
      </label>
      <label className="field">
        <span>Mobile</span>
        <input
          required
          autoComplete="tel"
          inputMode="tel"
          value={lead.mobile}
          onChange={(e) => setLead({ ...lead, mobile: e.target.value })}
        />
      </label>
      <label className="consent">
        <input type="checkbox" checked={privacy} onChange={(e) => setPrivacy(e.target.checked)} required />
        <span>
          I have read the{" "}
          {compliance.privacyUrl ? (
            <a href={compliance.privacyUrl} target="_blank" rel="noreferrer">
              Privacy Policy
            </a>
          ) : (
            "Privacy Policy"
          )}{" "}
          and I agree to be contacted about this home loan.
        </span>
      </label>
      {needGuide ? (
        <label className="consent">
          <input
            type="checkbox"
            checked={creditGuide}
            onChange={(e) => setCreditGuide(e.target.checked)}
            required
          />
          <span>
            I acknowledge the{" "}
            {compliance.creditGuideUrl ? (
              <a href={compliance.creditGuideUrl} target="_blank" rel="noreferrer">
                Credit Guide
              </a>
            ) : (
              "Credit Guide"
            )}
            . Credit assistance starts only if I ask a broker to act.
          </span>
        </label>
      ) : null}
      {error ? (
        <p className="form-error" role="alert">
          {error}
        </p>
      ) : null}
      <button className="ask" type="submit" disabled={sending}>
        {sending ? "Sending…" : submitLabel}
      </button>
    </form>
  );
}
